import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiSave, FiX, FiUser, FiPhone, FiCalendar,
  FiMapPin, FiInfo, FiMail, FiFileText, FiArrowLeft, FiCheckCircle,
  FiAlertCircle,
} from 'react-icons/fi';
import { api } from '@crm/shared';
import Layout from '../../components/Layout';
import { toast } from 'react-toastify';
import DateInput from '../../components/DateInput';
import { fmtDate } from '../../utils/dateUtils';

/* ─── Helpers ─── */
const today    = () => new Date().toISOString().split('T')[0];
const tomorrow = () => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0]; };
const extractArr = (res) => {
  const d = res?.data;
  if (Array.isArray(d?.results)) return d.results;
  if (Array.isArray(d?.data))    return d.data;
  if (Array.isArray(d))          return d;
  return [];
};

/* ─── Design tokens ─── */
const C = {
  navy:'#0D3B5E', navyD:'#082B47', navyL:'#EBF4FF',
  gold:'#F5A623',
  red:'#DC2626', redL:'#FEF2F2',
  border:'#E5E7EB', borderL:'#F3F4F6',
  white:'#FFFFFF', t1:'#111827', t2:'#374151', t3:'#6B7280', t4:'#9CA3AF',
};

/* ─── Field wrapper ─── */
const Field = ({ label, required, hint, error, children }) => (
  <div>
    <label style={{ display:'block', fontSize:12, fontWeight:700, color: error ? C.red : C.t2, marginBottom:6, letterSpacing:'0.01em' }}>
      {label}{required && <span style={{ color:C.red, marginLeft:2 }}>*</span>}
    </label>
    {children}
    {hint && !error && <div style={{ fontSize:11.5, color:C.t4, marginTop:4, lineHeight:1.5 }}>{hint}</div>}
    {error && (
      <div style={{ display:'flex', alignItems:'center', gap:4, fontSize:11.5, color:C.red, marginTop:4, fontWeight:600 }}>
        <FiAlertCircle size={11}/>{error}
      </div>
    )}
  </div>
);

/* ─── Section divider ─── */
const Section = ({ icon, title, desc }) => (
  <div style={{ display:'flex', alignItems:'center', gap:10, padding:'18px 0 12px', marginTop:8, borderBottom:`2px solid ${C.borderL}`, marginBottom:20 }}>
    <div style={{ width:34, height:34, borderRadius:9, background:C.navyL, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, color:C.navy }}>
      {icon}
    </div>
    <div>
      <h3 style={{ margin:0, fontSize:14.5, fontWeight:800, color:C.t1, letterSpacing:'-0.02em' }}>{title}</h3>
      {desc && <p style={{ margin:0, fontSize:12, color:C.t4, marginTop:1 }}>{desc}</p>}
    </div>
  </div>
);

/* ─── Shared input style ─── */
const makeInput = (hasError = false, extraStyle = {}) => ({
  display: 'block',
  width: '100%',
  height: 40,
  padding: '0 13px',
  border: `1.5px solid ${hasError ? C.red : C.border}`,
  borderRadius: 9,
  fontSize: 13.5,
  fontFamily: 'inherit',
  color: C.t1,
  background: '#FAFBFC',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'all 0.15s',
  ...extraStyle,
});

const onFocus = (e) => { e.target.style.borderColor = C.navy; e.target.style.background = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(13,59,94,0.08)'; };
const onBlur  = (e, hasError) => { e.target.style.borderColor = hasError ? C.red : C.border; e.target.style.background = '#FAFBFC'; e.target.style.boxShadow = 'none'; };

const BLANK_FORM = {
  customer_name: '', mobile_number: '', email: '', product_id: '', loan_amount: '',
  lead_source: '', is_bsa: false, bsa_id: '', internal_staff_id: '', location: '',
  next_follow_up_date: tomorrow(), remarks: '',
};

/* ═════════════════════════════════════════════════════════
   LEAD CREATE / EDIT
═════════════════════════════════════════════════════════ */
function useWW() {
  const [w, setW] = React.useState(window.innerWidth);
  React.useEffect(() => { const h = () => setW(window.innerWidth); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
  return w;
}

export default function LeadCreate() {
  const navigate = useNavigate();
  const { id }   = useParams();
  const isEdit   = Boolean(id);
  const isMobile = useWW() < 768;

  const [products,      setProducts]      = useState([]);
  const [bsaList,       setBsaList]       = useState([]);
  const [staffList,     setStaffList]     = useState([]);
  const [mastersLoading, setMastersLoading] = useState(true);
  const [editBlocked,   setEditBlocked]   = useState(false);
  const [editBlockReason, setEditBlockReason] = useState('');

  const [form,   setForm]   = useState(BLANK_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const safe = async (fn, label) => { try { return await fn(); } catch (e) { console.warn(`⚠️ ${label} failed:`, e); return null; } };
      const [pRes, bRes, uRes] = await Promise.all([
        safe(() => api.getProducts({ page_size: 200 }), 'products'),
        safe(() => api.getBsaList({ page_size: 200 }), 'bsa list'),
        safe(() => api.getUsers({ page_size: 200 }), 'users'),
      ]);
      setProducts(extractArr(pRes).filter(p => p.is_active !== false));
      setBsaList(extractArr(bRes).filter(b => b.is_active !== false));
      setStaffList(extractArr(uRes).map(u => ({
        id: u.id,
        name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || `User ${u.id}`,
      })));

      if (isEdit) {
        try {
          const res = await api.getLeadById(id);
          const lead = res?.data;
          if (lead?.id) {
            // ── SRS Edit Rules ──
            // BR-02: PDD Done leads — only admin can edit
            // CLOSED leads — no one can edit
            const stageRaw = lead.current_stage || '';
            const isClosed = stageRaw === 'CLOSED' || lead.is_closed === true;
            const isPdd    = stageRaw === 'PDD' || stageRaw === 'REVENUE';
            let blocked = false, blockReason = '';
            if (isClosed) {
              blocked = true;
              blockReason = 'Closed leads cannot be edited. The lead workflow is complete.';
            } else if (isPdd) {
              const storedUser = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
              // Handle role as object {id,name}, string, role_name, or Django is_superuser
              const _rObj = storedUser?.role;
              const _rName = (_rObj && typeof _rObj === 'object') ? (_rObj.name || '') : (storedUser?.role_name || (typeof _rObj === 'string' ? _rObj : ''));
              const _r = _rName.toLowerCase().trim();
              const isAdmin = _r === 'admin' || _r === 'administrator' || _r === 'super admin' || _r === 'superadmin'
                || storedUser?.is_superuser === true || storedUser?.is_staff === true;
              if (!isAdmin) {
                blocked = true;
                blockReason = 'This lead has reached PDD stage and is frozen for normal users (BR-02). Only Admin can edit PDD Done leads.';
              }
            }
            if (blocked) {
              setEditBlocked(true);
              setEditBlockReason(blockReason);
            }
            setForm({
              customer_name: lead.customer_name || '', mobile_number: lead.mobile_number || '',
              email: lead.email || '', product_id: lead.product?.id || lead.product_id || '',
              loan_amount: lead.loan_amount || '', lead_source: lead.lead_source || '',
              is_bsa: !!(lead.is_bsa || lead.bsa?.id || lead.bsa_id),
              bsa_id: lead.bsa?.id || lead.bsa_id || '',
              internal_staff_id: lead.internal_staff?.id || lead.internal_staff_id || '',
              location: lead.location || '',
              next_follow_up_date: lead.next_follow_up_date || tomorrow(),
              remarks: lead.remarks || '',
            });
          }
        } catch (err) { toast.error('Failed to load lead data'); }
      }
      setMastersLoading(false);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'lead_source') setForm(p => ({ ...p, lead_source: value.toUpperCase(), is_bsa: false, bsa_id: '', internal_staff_id: '' }));
    else setForm(p => ({ ...p, [name]: value }));
    if (errors[name]) setErrors(p => ({ ...p, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.customer_name.trim()) e.customer_name = 'Customer name is required';
    const mob = form.mobile_number.replace(/\D/g, '');
    if (!mob) e.mobile_number = 'Mobile number is required';
    else if (mob.length !== 10) e.mobile_number = 'Enter a valid 10-digit mobile number';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email address';
    if (!form.product_id) e.product_id = 'Product is required';
    // institution_id not in backend POST body — removed from validation
    if (!form.lead_source) e.lead_source = 'Lead source is required';
    if (form.is_bsa && !form.bsa_id) e.bsa_id = 'BSA is required';
    if (form.lead_source === 'INTERNAL' && !form.internal_staff_id) e.internal_staff_id = 'Staff member is required';
    if (!form.loan_amount) e.loan_amount = 'Loan amount is required';
    else if (isNaN(form.loan_amount) || Number(form.loan_amount) < 1) e.loan_amount = 'Amount must be greater than 0';
    if (!form.next_follow_up_date) e.next_follow_up_date = 'Next follow-up date is required';
    if (form.remarks && form.remarks.length > 500) e.remarks = 'Max 500 characters';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const getUser = () => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) { toast.error('Please fix the errors below'); return; }
    const user = getUser();
    // Only send fields the backend accepts — match POST body exactly
    const payload = {
      customer_name:      form.customer_name.trim(),
      mobile_number:      form.mobile_number.replace(/\D/g, ''),
      email:              form.email.trim() || null,
      loan_amount:        form.loan_amount,
      lead_source:        form.lead_source,
      is_bsa:             form.is_bsa,
      product_id:         Number(form.product_id),
      bsa_id:             form.is_bsa ? Number(form.bsa_id) : null,
      internal_staff_id:  form.lead_source === 'INTERNAL' ? Number(form.internal_staff_id) : null,
      location:           form.location.trim() || null,
      remarks:            form.remarks.trim() || null,
      next_follow_up_date: form.next_follow_up_date,
    };
    // CREATE: remove null fields (backend doesn't need them for new records)
    // EDIT: keep null fields — so backend clears old values (e.g. remarks cleared → null sent → backend saves null)
    if (!isEdit) {
      Object.keys(payload).forEach(k => { if (payload[k] === null || payload[k] === undefined) delete payload[k]; });
    }
    try {
      setSaving(true);
      if (isEdit) { await api.updateLead(id, payload); toast.success('Lead updated!'); navigate(`/leads/${id}`); }
      else        { await api.createLead(payload); toast.success('Lead created!'); navigate('/leads'); }
    } catch (err) {
      const data = err?.response?.data || err;
      const firstKey = Object.keys(data)[0];
      const msg = firstKey && Array.isArray(data[firstKey]) ? `${firstKey}: ${data[firstKey][0]}` : data?.message || data?.detail || `Failed to ${isEdit ? 'update' : 'create'} lead`;
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const errorCount = Object.values(errors).filter(Boolean).length;

  return (
    <>
      <style>{`
        @keyframes cfFadeUp { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin { to{transform:rotate(360deg)} }
        .cf-input-focus:focus { border-color: #0D3B5E !important; background: #fff !important; box-shadow: 0 0 0 3px rgba(13,59,94,0.08) !important; }
      `}</style>
      <Layout pageTitle={isEdit ? 'Edit Lead' : 'Create Lead'}>
        <div style={{ maxWidth:940, margin:'0 auto', fontFamily:"'Plus Jakarta Sans',sans-serif", animation:'cfFadeUp 0.22s ease both' }}>

          {/* ══════ PAGE BANNER ══════ */}
          <div style={{ borderRadius:14, padding:'18px 26px', background:'linear-gradient(120deg,#0D3B5E,#1A5276)', boxShadow:'0 4px 20px rgba(13,59,94,0.20)', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12, marginBottom:16, position:'relative', overflow:'hidden' }}>
            <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:'linear-gradient(90deg,#F5A623,#FBBF4A,transparent)' }}/>
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              <button onClick={() => navigate(isEdit ? `/leads/${id}` : '/leads')}
                style={{ width:38, height:38, borderRadius:10, background:'rgba(255,255,255,0.11)', border:'1px solid rgba(255,255,255,0.22)', color:'rgba(255,255,255,0.80)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0, transition:'all 0.12s' }}
                onMouseEnter={e=>{e.currentTarget.style.background='rgba(255,255,255,0.20)';}}
                onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,0.11)';}}>
                <FiArrowLeft size={17}/>
              </button>
              <div>
                <div style={{ fontSize:11, color:'rgba(255,255,255,0.45)', fontWeight:700, letterSpacing:'0.10em', textTransform:'uppercase', marginBottom:4 }}>Lead Management</div>
                <h2 style={{ margin:0, fontSize:22, fontWeight:800, color:'#fff', letterSpacing:'-0.03em', lineHeight:1.2 }}>
                  {isEdit ? 'Edit Lead' : 'Create New Lead'}
                </h2>
                {!isEdit && (
                  <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:6 }}>
                    <FiInfo size={11} style={{ color:'rgba(255,255,255,0.45)' }}/>
                    <span style={{ fontSize:12, color:'rgba(255,255,255,0.50)' }}>Status will be auto-set to</span>
                    <span style={{ padding:'1px 9px', borderRadius:999, fontSize:11.5, fontWeight:700, background:'rgba(245,166,35,0.22)', border:'1px solid rgba(245,166,35,0.40)', color:'#FBBF4A' }}>Follow Up</span>
                  </div>
                )}
              </div>
            </div>
            <button onClick={() => navigate(isEdit ? `/leads/${id}` : '/leads')}
              style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:9, background:'rgba(255,255,255,0.11)', border:'1px solid rgba(255,255,255,0.22)', color:'rgba(255,255,255,0.75)', fontWeight:600, fontSize:13, cursor:'pointer', fontFamily:'inherit', transition:'all 0.12s' }}
              onMouseEnter={e=>{e.currentTarget.style.background='rgba(255,255,255,0.20)';}}
              onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,0.11)';}}>
              <FiX size={14}/> Cancel
            </button>
          </div>

          {/* ══════ FORM CARD ══════ */}
          <div style={{ background:C.white, borderRadius:14, border:`1px solid ${C.border}`, boxShadow:'0 2px 8px rgba(13,59,94,0.07)', overflow:'hidden' }}>

            {/* Error summary bar */}
            {errorCount > 0 && (
              <div style={{ display:'flex', alignItems:'center', gap:9, padding:'10px 28px', background:C.redL, borderBottom:`1.5px solid #FECACA`, fontSize:13, color:C.red, fontWeight:600 }}>
                <FiAlertCircle size={14}/>
                {errorCount} field{errorCount!==1?'s':''} need attention — please review below
              </div>
            )}

            {/* ── Edit Blocked Banner (BR-02 / Closed) ── */}
            {editBlocked && (
              <div style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'14px 20px', background:'#FEF3DC', borderBottom:'2px solid #F0D798' }}>
                <div style={{ width:36, height:36, borderRadius:9, background:'#92400E20', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <FiAlertCircle size={17} style={{ color:'#92400E' }}/>
                </div>
                <div>
                  <div style={{ fontSize:13.5, fontWeight:800, color:'#92400E', marginBottom:3 }}>Edit Restricted</div>
                  <div style={{ fontSize:13, color:'#78350F', lineHeight:1.6 }}>{editBlockReason}</div>
                  <div style={{ fontSize:12, color:'#92400E', marginTop:6, fontWeight:600 }}>You can still view this lead. Use the <button type="button" onClick={() => navigate(`/leads/${id}`)} style={{ background:'none', border:'none', color:'#92400E', fontWeight:700, cursor:'pointer', fontSize:12, padding:0, textDecoration:'underline' }}>Lead Detail page</button> for workflow actions.</div>
                </div>
              </div>
            )}

            {mastersLoading ? (
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'72px 24px', gap:14 }}>
                <div style={{ width:40, height:40, borderRadius:'50%', border:`3px solid ${C.borderL}`, borderTopColor:C.navy, animation:'spin 0.8s linear infinite' }}/>
                <p style={{ color:C.t4, fontSize:13.5, margin:0 }}>{isEdit ? 'Loading lead data...' : 'Loading form...'}</p>
              </div>
            ) : (
              <form onSubmit={editBlocked ? e=>e.preventDefault() : handleSubmit} autoComplete="off" style={{ padding:'28px 32px 24px', opacity: editBlocked ? 0.6 : 1, pointerEvents: editBlocked ? 'none' : 'auto' }}>

                {/* ── SECTION 1: Customer Information ── */}
                <Section icon={<FiUser size={15}/>} title="Customer Information" desc="Basic contact details of the lead"/>
                <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', columnGap:24, rowGap:18, marginBottom:8 }}>

                  <Field label="Customer Name" required error={errors.customer_name}>
                    <div style={{ position:'relative' }}>
                      <FiUser size={14} style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', pointerEvents:'none', color:C.t4 }}/>
                      <input name="customer_name" value={form.customer_name} onChange={handleChange}
                        placeholder="Enter full name" autoComplete="off"
                        style={{ ...makeInput(!!errors.customer_name), paddingLeft:36 }}
                        onFocus={onFocus} onBlur={e=>onBlur(e,!!errors.customer_name)}/>
                    </div>
                  </Field>

                  <Field label="Mobile Number" required error={errors.mobile_number}>
                    <div style={{ position:'relative' }}>
                      <FiPhone size={14} style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', pointerEvents:'none', color:C.t4 }}/>
                      <input name="mobile_number" value={form.mobile_number} onChange={handleChange}
                        placeholder="10-digit mobile number" maxLength={10}
                        style={{ ...makeInput(!!errors.mobile_number), paddingLeft:36 }}
                        onFocus={onFocus} onBlur={e=>onBlur(e,!!errors.mobile_number)}/>
                    </div>
                  </Field>

                  <Field label="Email Address" hint="Optional" error={errors.email}>
                    <div style={{ position:'relative' }}>
                      <FiMail size={14} style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', pointerEvents:'none', color:C.t4 }}/>
                      <input name="email" type="email" value={form.email} onChange={handleChange}
                        placeholder="customer@example.com"
                        style={{ ...makeInput(!!errors.email), paddingLeft:36 }}
                        onFocus={onFocus} onBlur={e=>onBlur(e,!!errors.email)}/>
                    </div>
                  </Field>

                  <Field label="Location" hint="City or area (Optional)">
                    <div style={{ position:'relative' }}>
                      <FiMapPin size={14} style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', pointerEvents:'none', color:C.t4 }}/>
                      <input name="location" value={form.location} onChange={handleChange}
                        placeholder="e.g. Mumbai, Andheri"
                        style={{ ...makeInput(false), paddingLeft:36 }}
                        onFocus={onFocus} onBlur={e=>onBlur(e,false)}/>
                    </div>
                  </Field>
                </div>

                {/* ── SECTION 2: Lead Details ── */}
                <Section icon={<FiFileText size={15}/>} title="Lead Details" desc="Product, institution and source information"/>
                <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', columnGap:24, rowGap:18, marginBottom:8 }}>

                  <Field label="Product" required error={errors.product_id}>
                    <select name="product_id" value={form.product_id} onChange={handleChange}
                      style={makeInput(!!errors.product_id)}
                      onFocus={onFocus} onBlur={e=>onBlur(e,!!errors.product_id)}>
                      <option value="">— Select Product —</option>
                      {products.length === 0
                        ? <option disabled>No products available</option>
                        : products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)
                      }
                    </select>
                  </Field>

                  <Field label="Loan Amount (₹)" required error={errors.loan_amount}>
                    <div style={{ position:'relative' }}>
                      <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', pointerEvents:'none', color:C.t4, fontSize:13, fontWeight:700 }}>₹</span>
                      <input name="loan_amount" type="number" value={form.loan_amount} onChange={handleChange}
                        placeholder="e.g. 2500000" min="1" autoComplete="off"
                        style={{ ...makeInput(!!errors.loan_amount), paddingLeft:36 }}
                        onFocus={onFocus} onBlur={e=>onBlur(e,!!errors.loan_amount)}/>
                    </div>
                  </Field>

                  {/* Institution field removed — not in backend POST /api/leads/leads/ body */}

                  <Field label="Lead Source" required hint={isEdit ? undefined : '⚠️ Cannot be changed after creation'} error={errors.lead_source}>
                    <select name="lead_source" value={form.lead_source} onChange={handleChange}
                      disabled={isEdit}
                      style={{ ...makeInput(!!errors.lead_source), opacity: isEdit ? 0.55 : 1, cursor: isEdit ? 'not-allowed' : 'default' }}
                      onFocus={onFocus} onBlur={e=>onBlur(e,!!errors.lead_source)}>
                      <option value="">— Select Source —</option>
                      <option value="DIRECT">Direct</option>
                      <option value="INTERNAL">Internal</option>
                    </select>
                  </Field>

                  {/* is_bsa checkbox — shown for all source types */}
                  {!isEdit && (
                    <Field label="BSA Linked?" hint="Check if this lead came through a BSA">
                      <div style={{ display:'flex', alignItems:'center', gap:10, height:40, paddingLeft:4 }}>
                        <input
                          type="checkbox"
                          id="is_bsa"
                          checked={!!form.is_bsa}
                          onChange={e => {
                            setForm(p => ({ ...p, is_bsa: e.target.checked, bsa_id: '' }));
                            if (errors.bsa_id) setErrors(p => ({ ...p, bsa_id: '' }));
                          }}
                          style={{ width:16, height:16, cursor:'pointer', accentColor:'#0D3B5E' }}
                        />
                        <label htmlFor="is_bsa" style={{ fontSize:13, fontWeight:600, color:'#374151', cursor:'pointer' }}>
                          Yes — via BSA
                        </label>
                      </div>
                    </Field>
                  )}

                  {/* BSA dropdown — shown when is_bsa = true */}
                  {form.is_bsa && (
                    <Field label="BSA Name / Code" required error={errors.bsa_id}>
                      <select name="bsa_id" value={form.bsa_id} onChange={handleChange}
                        disabled={isEdit}
                        style={{ ...makeInput(!!errors.bsa_id), opacity: isEdit ? 0.55 : 1, cursor: isEdit ? 'not-allowed' : 'default' }}
                        onFocus={onFocus} onBlur={e=>onBlur(e,!!errors.bsa_id)}>
                        <option value="">— Select BSA —</option>
                        {bsaList.length === 0
                          ? <option disabled>No BSA records</option>
                          : bsaList.map(b => <option key={b.id} value={b.id}>{b.code ? `${b.code} — ${b.name}` : b.name}</option>)
                        }
                      </select>
                    </Field>
                  )}

                  {(form.lead_source === 'INTERNAL') && (
                    <Field label="Staff Member" required error={errors.internal_staff_id}>
                      <select name="internal_staff_id" value={form.internal_staff_id} onChange={handleChange}
                        style={makeInput(!!errors.internal_staff_id)}
                        onFocus={onFocus} onBlur={e=>onBlur(e,!!errors.internal_staff_id)}>
                        <option value="">— Select Staff Member —</option>
                        {staffList.length === 0
                          ? <option disabled>No staff available</option>
                          : staffList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)
                        }
                      </select>
                    </Field>
                  )}

                  {/* NOTE: Status is NOT editable from this form.
                       Status changes happen via LeadDetail Workflow tab (stage-based actions).
                       SRS: status progresses through Login→Sanction→Disbursement→PDD via dedicated stage forms. */}
                </div>

                {/* ── SECTION 3: Follow-up & Remarks ── */}
                <Section icon={<FiCalendar size={15}/>} title="Follow-up & Remarks" desc="Schedule next contact and add any notes"/>
                <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', columnGap:24, rowGap:18 }}>

                  <Field label="Date of Lead" hint="Auto-set to today">
                    <div style={{ position:'relative' }}>
                      <FiCalendar size={14} style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', pointerEvents:'none', color:C.t4 }}/>
                      <input value={fmtDate(today())} readOnly
                        style={{ ...makeInput(false), paddingLeft:36, opacity:0.55, cursor:'not-allowed', background:'#F3F4F6' }}/>
                    </div>
                  </Field>

                  <Field label="Next Follow-up Date" required error={errors.next_follow_up_date}>
                    <DateInput name="next_follow_up_date" value={form.next_follow_up_date}
                      onChange={e => { setForm(p => ({ ...p, next_follow_up_date: e.target.value })); if (errors.next_follow_up_date) setErrors(p => ({ ...p, next_follow_up_date:'' })); }}
                      style={{ ...makeInput(!!errors.next_follow_up_date), paddingLeft:12, paddingRight:36 }}
                      onFocus={onFocus} onBlur={e=>onBlur(e,!!errors.next_follow_up_date)}/>
                  </Field>
  
                  <div style={{ gridColumn:'span 2' }}>
                    <Field
                      label={`Remarks${form.remarks.length > 0 ? ` (${form.remarks.length}/500)` : ' — Optional'}`}
                      error={errors.remarks}>
                      <textarea name="remarks" value={form.remarks} onChange={handleChange}
                        rows={3} maxLength={500}
                        placeholder="Any initial notes or comments about this lead..." autoComplete="off"
                        style={{ ...makeInput(!!errors.remarks), height:'auto', paddingTop:11, paddingBottom:11, resize:'vertical', lineHeight:1.65 }}
                        onFocus={onFocus} onBlur={e=>onBlur(e,!!errors.remarks)}/>
                    </Field>
                  </div>
                </div>

                {/* ── ACTION BAR ── */}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:24, marginTop:20, borderTop:`1.5px solid ${C.borderL}` }}>
                  <div style={{ fontSize:12.5, color:C.t4 }}>
                    {errorCount > 0
                      ? <span style={{ color:C.red, fontWeight:600, display:'flex', alignItems:'center', gap:5 }}><FiAlertCircle size={13}/> {errorCount} error{errorCount!==1?'s':''} to fix</span>
                      : <span style={{ color:'#16A34A', fontWeight:500, display:'flex', alignItems:'center', gap:5 }}><FiCheckCircle size={13}/> All required fields filled</span>
                    }
                  </div>
                  <div style={{ display:'flex', gap:10 }}>
                    <button type="button" onClick={() => navigate(isEdit ? `/leads/${id}` : '/leads')}
                      className="btn-ghost" style={{ fontSize:14 }}>
                      <FiX size={14}/> Cancel
                    </button>
                    <button type="submit" disabled={saving}
                      style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'10px 24px', borderRadius:10, background: saving ? '#9CA3AF' : C.gold, color: saving ? '#fff' : '#0D1B2A', border:'none', fontWeight:800, fontSize:14, cursor: saving ? 'not-allowed' : 'pointer', fontFamily:'inherit', boxShadow: saving ? 'none' : '0 4px 14px rgba(245,166,35,0.40)', transition:'all 0.14s' }}
                      onMouseEnter={e=>{ if (!saving) { e.currentTarget.style.transform='translateY(-1px)'; e.currentTarget.style.background='#FBBF4A'; }}}
                      onMouseLeave={e=>{ e.currentTarget.style.transform=''; e.currentTarget.style.background= saving ? '#9CA3AF' : C.gold; }}>
                      {saving ? (
                        <>
                          <div style={{ width:16, height:16, borderRadius:'50%', border:'2px solid rgba(255,255,255,0.40)', borderTopColor:'#fff', animation:'spin 0.7s linear infinite', flexShrink:0 }}/>
                          Saving...
                        </>
                      ) : (
                        <><FiSave size={14}/> {isEdit ? 'Update Lead' : 'Create Lead'}</>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </Layout>
    </>
  );
}